import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, ShoppingCart, Package, FileX, FileText, FileSpreadsheet, ArrowRight, Save, IndianRupee, ArrowLeft } from 'lucide-react';
import { Tabs } from '../../../components/ui';
import { Button } from '../../../components/ui/Button';

import { ResizableSplitPane } from '../components/ResizableSplitPane';
import { BudgetSearchFilter } from '../components/orderingtSearchFilter';
import { PdfViewer } from '../components/pdfviewer';
import { XlViewer } from '../components/xl_viewer';
import { InitialQuotationMaker } from '../components/ordering_quotation_maker';
import { QuotationPreviewModal } from '../components/ordering_quotation_preview';
import { OrderingTableView } from '../components/ordering_table_view';
import { QuotationPrepScreen } from './QuotationPrepScreen';

import { documentService } from '../../../services/documentService';
import { fetchPurchases } from '../../inventory/pages/InventoryPage/services';
import { API_BASE_URL } from '../../../services/api';
import { format } from 'date-fns';
import { orderingQuotationService } from '../services/orderingQuotationService';
import { showSuccessMessage, showAxiosError } from '../../../utils/toast';

const MAIN_TABS = [
  { id: 'ordering', label: 'Master Order', icon: ShoppingCart },
  { id: 'inventory', label: 'Order Prep', icon: Calculator }
];

const INVENTORY_TABS = [];

const ORDERING_TABS = [];

const resolveContactRef = (contactObj, stateObj) => {
  const contact = contactObj || stateObj || {};
  const isDirect = contact.isDirectSale || contact.salesType === 'Direct Sale' || stateObj?.isDirectSale || stateObj?.salesType === 'Direct Sale';

  const assignee = contact.assignedTo || contact.assignee || contact.telecaller || stateObj?.assignedTo || stateObj?.assignee;
  let assigneeName = '';
  if (assignee) {
    if (typeof assignee === 'string' && !/^[0-9a-fA-F]{24}$/.test(assignee)) assigneeName = assignee;
    else if (typeof assignee === 'object') assigneeName = assignee.name || `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim() || '';
  }

  if (isDirect) return assigneeName || 'Direct Sale';

  const providedRef = stateObj?.contactRef || contact.customId;
  if (providedRef && !/^[0-9a-fA-F]{24}$/.test(String(providedRef))) return providedRef;

  return assigneeName || (providedRef || '');
};

const OrderingScreen = ({ embedded = false, initialData = null, onBack = null, onSuccess = null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const stateData = initialData || location.state;

  const [activeMainTab, setActiveMainTab] = useState(() => {
    if (embedded && stateData) return 'inventory';
    return window.location.pathname.includes('/create') ? 'inventory' : 'ordering';
  });
  const [activeInventoryTab, setActiveInventoryTab] = useState('catalog');
  const [activeOrderingTab, setActiveOrderingTab] = useState('appliances');

  const [customTableTotal, setCustomTableTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("Catalog");
  const [stockSelections, setStockSelections] = useState({});

  const [catalogDocs, setCatalogDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [quotationDetails, setQuotationDetails] = useState({
    projectName: '', site: '', contactRef: '', date: new Date().toLocaleDateString('en-GB'), quotationNo: '', referenceDesigner: '', category: 'Appliances',
    docType: location.state?.docType || 'Order'
  });

  const [quotationTableData, setQuotationTableData] = useState({
    rows: [], subTotal: 0, discountAmount: 0, gstAmount: 0, grandTotal: 0,
    discountType: 'percentage', discountValue: 0, gstType: 'percentage', gstValue: 0
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    if (stateData?.createNewQuotationForLead) {
      const lead = stateData.createNewQuotationForLead;
      const leadId = stateData.leadId || lead._id || lead.id || lead.customId;
      const customerName = lead.customerName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.projectName || '';
      const siteStr = `${lead.city || ''}, ${lead.state || ''}`.replace(/^,\s*|,\s*$/g, '') || lead.addressLine1 || lead.site || '';

      setQuotationTableData({
        rows: [], subTotal: 0, discountAmount: 0, gstAmount: 0, grandTotal: 0,
        discountType: 'percentage', discountValue: 0, gstType: 'percentage', gstValue: 0
      });
      setCustomTableTotal(0);

      setQuotationDetails({
        id: null,
        projectName: customerName,
        site: siteStr,
        contactRef: resolveContactRef(lead.contactObj || lead, stateData),
        leadId: leadId,
        clientId: leadId,
        date: new Date().toLocaleDateString('en-GB'),
        quotationNo: '',
        referenceDesigner: lead.referenceDesigner || lead.designer || '',
        category: 'Appliances',
        docType: location.state?.docType || 'Order'
      });
      setActiveMainTab('inventory');
      if (!embedded) window.history.replaceState({}, document.title);
    } else if (stateData?.editQuotation) {
      handleEditQuotation(stateData.editQuotation);
      if (!embedded) window.history.replaceState({}, document.title);
    } else if (stateData) {
      const contact = stateData.contactObj || stateData;
      const customerName = stateData.projectName || stateData.clientName || contact.customerName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || '';
      const phone = contact.phoneNumber || contact.phone || contact.mobile || stateData.phoneNumber || stateData.phone || '';
      const nameWithPhone = phone && !customerName.includes(phone) ? `${customerName} - ${phone}` : customerName;
      const siteStr = stateData.site || `${contact.city || ''}, ${contact.state || ''}`.replace(/^,\s*|,\s*$/g, '') || contact.addressLine1 || contact.site || '';
      const contactRefStr = resolveContactRef(contact, stateData);
      const designerStr = stateData.referenceDesigner || stateData.designer || contact.referenceDesigner || contact.designer || '';

      const isExistingQuote = stateData.quotationNo && stateData.quotationNo !== '-' && Array.isArray(stateData.rows);
      if (isExistingQuote) {
        setQuotationDetails({
          id: stateData._id || stateData.id,
          projectName: stateData.projectName || nameWithPhone,
          site: stateData.site || siteStr,
          contactRef: contactRefStr,
          date: stateData.date || new Date().toLocaleDateString('en-GB'),
          quotationNo: stateData.quotationNo,
          referenceDesigner: stateData.referenceDesigner || designerStr,
          category: stateData.category || 'Appliances',
          docType: stateData.docType || location.state?.docType || 'Order',
          leadId: contact._id || stateData._id,
          clientId: contact._id || stateData._id
        });
        setQuotationTableData({
          rows: stateData.rows || [],
          subTotal: stateData.totals?.subTotal || 0,
          discountAmount: stateData.totals?.discountAmount || 0,
          gstAmount: stateData.totals?.gstAmount || 0,
          grandTotal: stateData.totals?.grandTotal || 0,
          discountType: stateData.totals?.discountType || 'percentage',
          discountValue: stateData.totals?.discountValue || 0,
          gstType: stateData.totals?.gstType || 'percentage',
          gstValue: stateData.totals?.gstValue || 0
        });
      } else {
        setQuotationDetails(prev => ({
          ...prev,
          projectName: nameWithPhone || prev.projectName,
          site: siteStr || prev.site,
          contactRef: contactRefStr || prev.contactRef,
          referenceDesigner: designerStr || prev.referenceDesigner,
          date: prev.date || new Date().toLocaleDateString('en-GB'),
          docType: location.state?.docType || prev.docType || 'Order',
          leadId: contact._id || stateData._id || prev.leadId,
          clientId: contact._id || stateData._id || prev.clientId
        }));
      }
      setActiveMainTab('inventory');
      if (!embedded) window.history.replaceState({}, document.title);
    }
  }, [stateData, embedded]);

  useEffect(() => {
    // If quotationNo is missing (whether new quote or draft without number)
    if (!quotationDetails.quotationNo) {
      orderingQuotationService.getNextQuotationNo(quotationDetails.category || 'Appliances')
        .then(res => {
          if (res && res.data && res.data.quotationNo) {
            setQuotationDetails(prev => ({
              ...prev,
              quotationNo: res.data.quotationNo,
              date: prev.date || new Date().toLocaleDateString('en-GB')
            }));
          } else {
            const letter = (quotationDetails.category || 'Appliances').charAt(0).toUpperCase();
            const year = new Date().getFullYear();
            setQuotationDetails(prev => ({
              ...prev,
              quotationNo: prev.quotationNo || `VK${year}${letter}00001`,
              date: prev.date || new Date().toLocaleDateString('en-GB')
            }));
          }
        })
        .catch(err => {
          console.error("Failed to fetch next ordering quotation no:", err);
          const letter = (quotationDetails.category || 'Appliances').charAt(0).toUpperCase();
          const year = new Date().getFullYear();
          setQuotationDetails(prev => ({
            ...prev,
            quotationNo: prev.quotationNo || `VK${year}${letter}00001`,
            date: prev.date || new Date().toLocaleDateString('en-GB')
          }));
        });
    } else if (quotationDetails.quotationNo.startsWith('VK')) {
      // If the user switches category (e.g. Appliances to Transport),
      // replace the category letter (7th char in VKYYYYXNNNNN format) without consuming another sequence!
      const currentNo = quotationDetails.quotationNo;
      const catLetter = (quotationDetails.category || 'Appliances').charAt(0).toUpperCase();
      if (currentNo.length === 12 && currentNo.charAt(6) !== catLetter) {
        const updatedNo = currentNo.slice(0, 6) + catLetter + currentNo.slice(7);
        setQuotationDetails(prev => ({ ...prev, quotationNo: updatedNo }));
      }
    }
  }, [quotationDetails.category, quotationDetails.quotationNo]);

  useEffect(() => {
    if (activeMainTab !== 'inventory') return;
    const fetchCatalogDocs = async () => {
      if (filterType === 'Stock') {
        setLoadingDocs(true);
        try {
          const res = await fetchPurchases();
          const pList = res?.data?.data || res?.data || (Array.isArray(res) ? res : []);
          const filtered = Array.isArray(pList) ? pList.filter(item => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const vendorName = item.vendorName || (typeof item.vendor === 'object' ? (item.vendor?.vendorName || item.vendor?.name) : item.vendor) || '';
            const itemName = item.item?.itemName || item.item?.name || item.description || '';
            const invoiceNo = item.invoiceNumber || item.invoiceNo || '';
            return vendorName.toLowerCase().includes(q) || itemName.toLowerCase().includes(q) || invoiceNo.toLowerCase().includes(q);
          }) : [];
          setCatalogDocs(filtered);
        } catch (err) {
          console.error("Failed to fetch purchases:", err);
          setCatalogDocs([]);
        } finally {
          setLoadingDocs(false);
        }
        return;
      }
      
      setLoadingDocs(true);
      try {
        const response = await documentService.getDocuments({
          isRelatedToCatalog: true,
          category: 'Ordering',
          search: searchQuery,
          formatFilter: filterType === 'Catalog' ? 'All' : filterType,
        });
        if (response.success) {
          setCatalogDocs(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch catalog documents:", error);
      } finally {
        setLoadingDocs(false);
      }
    };
    fetchCatalogDocs();
  }, [searchQuery, filterType, activeMainTab]);

  const handleCancelQuotation = () => {
    navigate('/ordering');
  };

  const handleEditQuotation = (row) => {
    setQuotationDetails({
      id: row._id,
      projectName: row.projectName || '',
      site: row.site || '',
      contactRef: row.contactObj?.customId || row.contactRef || row.leadId || row.clientId || '',
      leadId: row.leadId || row.contactRef || null,
      clientId: row.clientId || row.contactRef || null,
      date: row.date || '',
      quotationNo: row.quotationNo || '',
      referenceDesigner: row.referenceDesigner || '',
      category: row.category || 'Appliances',
      docType: row.docType || 'Order'
    });
    setQuotationTableData({
      rows: row.rows || [],
      subTotal: row.totals?.subTotal || 0,
      discountAmount: row.totals?.discountAmount || 0,
      gstAmount: row.totals?.gstAmount || 0,
      grandTotal: row.totals?.grandTotal || 0,
      discountType: row.totals?.discountType || 'percentage',
      discountValue: row.totals?.discountValue || 0,
      gstType: row.totals?.gstType || 'percentage',
      gstValue: row.totals?.gstValue || 0,
    });
    setActiveMainTab('inventory');
  };

  useEffect(() => {
    const stockRows = Object.values(stockSelections).map(({ flatItem, qty }) => ({
      id: `stock-${flatItem._key}`,
      type: 'item',
      _fromStock: true,
      particulars: flatItem.itemName,
      quantity: String(qty || 1),
      colourType: '',
      warranty: '',
      amount: (flatItem.purchaseRate * (qty || 1)) || flatItem.amount
    }));

    setQuotationTableData(prev => {
      const manualRows = (prev.rows || []).filter(r => !r._fromStock);
      return { ...prev, rows: [...manualRows, ...stockRows] };
    });
  }, [stockSelections]);

  const handleSaveQuotation = async (pdfBlob = null, sendToOwner = false, paymentData = null) => {
    try {
      const payload = {
        ...quotationDetails,
        sendToOwner,
        rows: quotationTableData.rows,
        totals: {
          subTotal: quotationTableData.subTotal,
          discountAmount: quotationTableData.discountAmount,
          gstAmount: quotationTableData.gstAmount,
          grandTotal: quotationTableData.grandTotal,
          discountType: quotationTableData.discountType,
          discountValue: quotationTableData.discountValue,
          gstType: quotationTableData.gstType,
          gstValue: quotationTableData.gstValue
        },
        // Include payment details recorded by the user at submission time
        paymentData: paymentData || null
      };

      const formData = new FormData();
      formData.append('quotationData', JSON.stringify(payload));
      if (pdfBlob) {
        formData.append('pdfFile', pdfBlob, `Quotation_${quotationDetails.quotationNo || Date.now()}.pdf`);
      }

      const response = await orderingQuotationService.saveQuotation(formData);
      if (response.success) {
        setShowPreview(false);
        setShowSuccessModal(true);
      } else {
        showAxiosError(new Error('Failed to save quotation. Please try re-uploading.'));
      }
    } catch (error) {
      console.error(error);
      showAxiosError(error, 'Error occurred while saving. Please try re-uploading.');
    }
  };

  const totalBudget = customTableTotal;

  const renderInventoryTabContent = () => (
    <QuotationPrepScreen
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterType={filterType}
      setFilterType={setFilterType}
      loadingDocs={loadingDocs}
      catalogDocs={catalogDocs}
      stockSelections={stockSelections}
      setStockSelections={setStockSelections}
      selectedDocument={selectedDocument}
      setSelectedDocument={setSelectedDocument}
      quotationDetails={quotationDetails}
      setQuotationDetails={setQuotationDetails}
      quotationTableData={quotationTableData}
      setQuotationTableData={setQuotationTableData}
      customTableTotal={customTableTotal}
      setCustomTableTotal={setCustomTableTotal}
      showPreview={showPreview}
      setShowPreview={setShowPreview}
      handleSaveQuotation={handleSaveQuotation}
      handleCancelQuotation={handleCancelQuotation}
    />
  );

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-500">
      <div className="flex flex-col gap-2">
        {!embedded && (
          <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm mb-2">
            <div className="pl-8 z-20">
              <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
                Order Management
              </h1>
              <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Order Management</p>
            </div>
            <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
              <img
                src="/kitchens/1.jpg"
                alt="Order Management"
                className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
              />
            </div>
          </div>
        )}

        {/* Main Tabs commented out to enforce workflow: Master Order -> Workspace -> New Quotation */}
        {/* <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 overflow-hidden">
          <Tabs
            tabs={MAIN_TABS}
            activeTab={activeMainTab}
            onChange={setActiveMainTab}
            variant="main"
            className="w-full !border-b-0"
          />
        </div> */}

        {/* Sub Tabs and Content */}
        <div className="flex flex-col mt-1">
          {(activeMainTab === 'inventory' ? INVENTORY_TABS : ORDERING_TABS).length > 0 && (
            <Tabs
              tabs={activeMainTab === 'inventory' ? INVENTORY_TABS : ORDERING_TABS}
              activeTab={activeMainTab === 'inventory' ? activeInventoryTab : activeOrderingTab}
              onChange={activeMainTab === 'inventory' ? setActiveInventoryTab : setActiveOrderingTab}
              variant="sub"
              className="mb-2"
            />
          )}

          <div className="min-h-[400px]">
            {activeMainTab === 'inventory' ? (
              <div className="flex flex-col h-full">
                {/* Standalone Top Navigation Bar exactly like LeadOrderingWorkspace */}
                {!embedded && (
                  <div className="flex items-center justify-between mb-4">
                    <button
                      type="button"
                      onClick={handleCancelQuotation}
                      className="inline-flex items-center gap-2 px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-700 rounded-xl border border-slate-200/80 shadow-sm font-medium text-sm transition-all cursor-pointer"
                    >
                      <ArrowLeft size={16} />
                      <span>Back to Master Orders</span>
                    </button>
                  </div>
                )}
                {renderInventoryTabContent()}
              </div>
            ) : (
              <OrderingTableView activeTab={activeOrderingTab} onEdit={handleEditQuotation} onAddOrder={() => setActiveMainTab('inventory')} />
            )}
          </div>
          {showSuccessModal && (
            <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full flex flex-col items-center text-center animate-in zoom-in duration-300">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Success!</h3>
                <p className="text-slate-600 mb-8 text-base">
                  Your quotation has been successfully generated and saved to the database.
                </p>
                <Button
                  variant="primary"
                  className="w-full h-12 text-lg !bg-emerald-600 hover:!bg-emerald-700 !border-emerald-600 !text-white"
                  onClick={() => {
                    setShowSuccessModal(false);
                    if (onSuccess) {
                      onSuccess();
                    } else if (onBack) {
                      onBack();
                    } else {
                      navigate('/budget');
                    }
                  }}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderingScreen;

